<!--
@component
Menu

See markup below.
-->

<script lang="ts">
  import MatchesRail from './MatchesRail/index.svelte';
  import { scaleLinear } from 'd3-scale';
  import { appState } from '$lib/stores/index.svelte';
  import { page } from '$app/state';
  import { beforeNavigate } from '$app/navigation';
  import { inview } from 'svelte-inview';
  import scrollDirection from './scrollDirection';
  import { Block } from '@reuters-graphics/graphics-components';
  import TeamsMenu from './TeamsMenu/index.svelte';
  import Logo from './Logo/index.svelte';

  type MenuItem = {
    label: 'Groups' | 'Bracket' | 'Schedule' | 'Teams';
    url: string;
  };

  const TEAMS_DROPDOWN_ID = 'menu-teams-dropdown';

  // Maps the navbar texture width (%) to window width for responsive scaling.
  const scaleBg = scaleLinear()
    .domain([330, 1400])
    .range([300, 100])
    .clamp(true);

  // All possible top-level menu items. `Groups` and `Bracket` are mutually
  // exclusive — only one is shown depending on tournament phase.
  const allItems: readonly MenuItem[] = [
    { label: 'Schedule', url: `${appState.linkBase}/schedule/` },
    {
      label: 'Groups',
      url: `${appState.linkBase}/groups/standings/`,
    },
    { label: 'Bracket', url: `${appState.linkBase}/knockouts/` },
    { label: 'Teams', url: '' },
  ] as const;

  // The menu items list never changes at runtime — visibility on mobile is
  // handled via CSS so the layout stays in sync with the viewport without
  // an SSR/hydration flash.
  const visibleItems = $derived(allItems);

  // Bracket entry surfaced separately next to the logo on mobile.
  const bracket = allItems.find((d) => d.label === 'Bracket');

  // Map menu labels to their corresponding first URL path segment so the
  // active state correctly highlights items whose URL slug differs from
  // the visible label (e.g. "Bracket" lives at /knockouts/).
  const LABEL_TO_PATH: Record<string, string> = {
    bracket: 'knockouts',
    schedule: 'schedule',
    groups: 'groups',
    teams: 'teams',
  };

  // Extract the active page (first path segment after the base) from the
  // current URL using Svelte 5 $derived rune so it stays reactive.
  const activePage = $derived.by(() => {
    // Typed as plain `string` so we can reassign after `.slice()` /
    // `.replace()` (SvelteKit types `page.url.pathname` as a strict
    // route literal which wouldn't accept those return values).
    let path: string = page.url.pathname;

    // Remove the linkBase prefix if present. `appState.linkBase` already
    // accounts for both SvelteKit's base path and the `/embeddables`
    // segment used when rendering inside an embed, so stripping it is
    // enough to leave just the in-app route path.
    const linkBase = appState.linkBase;
    if (linkBase && path.startsWith(linkBase)) {
      path = path.slice(linkBase.length);
    }

    // Remove leading/trailing slashes and get the first segment
    path = path.replace(/^\/+|\/+$/g, '');
    const segments = path.split('/');
    return (segments[0] || '').toLowerCase();
  });

  const isActive = (label: string) =>
    activePage === LABEL_TO_PATH[label.toLowerCase()];

  // Whether the Teams dropdown is open.
  let showDropdownMenu = $state(false);
  // Index of the keyboard-focused item in the Teams dropdown.
  let active = $state(-1);

  beforeNavigate(() => {
    showDropdownMenu = false;
  });

  const toggleDropdown = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    showDropdownMenu = !showDropdownMenu;
    active = -1;
  };

  const closeDropdown = () => {
    showDropdownMenu = false;
  };

  // Lock page scroll while the Teams dropdown is open so users can't scroll
  // the page beneath the open menu. Restores scrolling on close / unmount.
  $effect(() => {
    if (typeof document === 'undefined') return;
    if (showDropdownMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  });

  // Toggles the sticky `pin` class once the menu scrolls out of view.
  // The menu uses `position: sticky` while pinned, so it must NOT be wrapped
  // in another block that would form a too-short containing block (in this
  // file the `.menu` element is a direct child of <Article> for that reason).
  const handleInViewChange = (event: CustomEvent) => {
    const { inView, node } = event.detail;
    if (window.scrollY > -1) node.classList.toggle('pin', !inView);
  };
</script>

<svelte:window
  onclick={() => {
    showDropdownMenu = false;
  }}
/>
<!--
  Note: this `<div class="menu">` is intentionally NOT wrapped in a
  `<Block>` (or any other block-level container) so that when it becomes
  `position: sticky`, its containing block is the parent <Article> element,
  giving sticky positioning enough scroll range to engage. Wrapping it in
  `<Block width="fluid">` breaks the sticky behaviour because the block has
  no extra height to act as the sticky container.
-->
<div
  id="top-menu"
  class="menu relative up"
  class:loading={appState.isLoading}
  class:open={showDropdownMenu}
  style="--w: {scaleBg(appState.windowWidth)}%"
  use:scrollDirection
  use:inview={{ root: null, threshold: 1 }}
  oninview_change={handleInViewChange}
>
  <nav
    class="up w-full flex flex-col justify-center items-center"
    class:open={showDropdownMenu}
  >
    <Block width="wider" class="relative flex navbar">
      <!-------------------->
      <!-- Tounament Logo -->
      <!-------------------->
      <div class="logo-wrapper">
        <!-- <a href="{appState.linkBase}/"> <Logo /></a> -->
        <!--
          Mobile-only Bracket link rendered next to the logo. Hidden via CSS
          on viewports >= 500px so the layout doesn't depend on JS state
          and there's no SSR/hydration flash.
        -->
        <!-- {#if bracket}
          <div
            class="tab-bracket-mobile"
            class:activeMenuItem={isActive(bracket.label)}
          >
            <a
              class="font-sans text-sm flex justify-center items-center tracking-wide"
              href={bracket.url}
              aria-current={isActive(bracket.label) ? 'page' : undefined}
              onclick={closeDropdown}
            >
              {bracket.label}
            </a>
          </div>
        {/if} -->
      </div>

      <ol class="flex justify-center items-center text-center">
        {#each visibleItems as item (item.label)}
          {#if item.label === 'Teams'}
            <!--------------------->
            <!-- Teams menu item -->
            <!--------------------->
            <li
              class="tab-{item.label.toLocaleLowerCase()} teams-dropdown flex flex-col justify-center items-center"
              class:activeMenuItem={isActive('teams')}
            >
              <button
                type="button"
                class="dropdown-toggle flex justify-center items-center"
                aria-haspopup="true"
                aria-expanded={showDropdownMenu}
                aria-controls={TEAMS_DROPDOWN_ID}
                aria-current={isActive('teams') ? 'page' : undefined}
                onclick={toggleDropdown}
              >
                <span class="label font-sans text-sm tracking-wide">
                  Teams
                </span>
                <svg
                  class="arrow mt-1"
                  class:up={showDropdownMenu}
                  focusable="false"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 11 7"
                >
                  <path
                    d="M10.413.587a.71.71 0 0 0-1.027 0L5.5 4.474 1.613.587a.71.71 0 0 0-1.027 0 .709.709 0 0 0 0 1.027l4.4 4.4c.147.147.367.22.513.22a.79.79 0 0 0 .513-.22l4.4-4.4a.71.71 0 0 0 0-1.027h.001Z"
                  />
                </svg>
              </button>
            </li>
          {:else}
            <!---------------------->
            <!-- Other menu items -->
            <!---------------------->
            <li
              class="tab-{item.label.toLocaleLowerCase()} flex flex-col justify-center items-center"
              class:activeMenuItem={isActive(item.label)}
            >
              <a
                class="font-sans text-sm flex justify-center items-center tracking-wide"
                href={item.url}
                aria-current={isActive(item.label) ? 'page' : undefined}
                onclick={closeDropdown}
              >
                {item.label}
              </a>
            </li>
          {/if}
        {/each}
      </ol>
    </Block>

    <!------------------------------>
    <!-- Dropdown menu for teams -->
    <!------------------------------>
    {#if showDropdownMenu}
      <Block width="widest" class="relative overflow-x-hidden">
        <div id={TEAMS_DROPDOWN_ID} role="region" aria-label="Teams">
          <TeamsMenu bind:showDropdownMenu bind:active />
        </div>
      </Block>
    {/if}
  </nav>

  <!-- Live and next matches rail -->
  <div class="rail fmx-3 py-0.5">
    <MatchesRail />
  </div>
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  $mobile-width: 660px;

  .rail {
    @include mixins.soft-shadow;
    @media (max-width: 600px) {
      :global(.match-rail-wrapper) {
        @include mixins.fmt-1;
      }
    }
  }

  .menu {
    position: relative;
    z-index: 1000;
    // backdrop-filter: blur(25px);
    transition: transform 0.3s ease-out;

    @include mixins.article-fluid;

    &:global(.pin) {
      position: sticky !important;
      top: -1px;
      left: -50%;
    }

    &:global(.pin.down:not(.open)) {
      transform: translate3d(0, -100%, 0);
    }

    &:global(.pin.up) {
      transform: translate3d(0, 0, 0);
    }
  }

  nav {
    --nav-height: 75px;
    --nav-item-width: 120px;

    @media (max-width: $mobile-width) {
      --nav-height: 55px;
      --nav-item-width: min(calc((100vw - 30px) / 3), 120px);
    }

    background-image: linear-gradient(
      to right,
      var(--theme-colour-background) 10%,
      var(--theme-colour-muted-grey) 50%,
      var(--theme-colour-background) 90%
    );
    position: relative;
    margin-inline: auto;

    &:before {
      content: '';
      pointer-events: none;
      background-image: url('$images/branding/navbar-texture.svg');
      mix-blend-mode: multiply;
      background-repeat: repeat-y;
      background-size: var(--w);
      height: var(--nav-height);
      width: 100%;
      position: absolute;
      top: 0;
      background-position: center top;

      @media (max-width: mixins.$column-width-wider) {
        background-position-x: 20%;
      }
      @media (max-width: $mobile-width) {
        height: calc(2 * var(--nav-height));
      }
    }

    :global(.navbar) {
      @media (max-width: $mobile-width) {
        flex-direction: column;
      }
    }
  }

  .logo-wrapper {
    height: var(--nav-height);

    @media (max-width: $mobile-width) {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
    }
  }

  .tab-bracket-mobile {
    display: none !important;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    @media (max-width: $mobile-width) {
      display: flex !important;
      margin-left: auto;
    }
  }

  // Hide the Bracket <li> inside the main menu on mobile (it's shown next
  // to the logo instead).
  // .tab-bracket {
  //   @media (max-width: $mobile-width) {
  //     display: none !important;
  //   }
  // }

  button {
    text-decoration: none;
    color: inherit;
  }

  ol,
  li {
    padding: 0;
    margin-block: 0;
    list-style: none;
  }

  li {
    height: var(--nav-height);
    border: none;
    position: relative;
  }
  [class*='tab-'] {
    &:after {
      content: '';
      display: block;
      // position: absolute;
      width: 0%;
      height: 3px;
      bottom: 0;
      background-color: var(--theme-colour-logo);
      transition: width 0.15s ease-out;
    }

    &:hover,
    &:active,
    &.activeMenuItem {
      @include mixins.font-medium;
      color: var(--theme-colour-logo);
      &:after {
        width: 100%;
      }
    }
  }

  a {
    width: var(--nav-item-width);
    text-decoration: none;
    height: var(--nav-height);
  }

  .teams-dropdown:after {
    content: '';
    display: block;
    position: absolute;
    width: 0%;
    height: 3px;
    bottom: 0;
    background-color: var(--theme-colour-logo);
    transition: width 0.15s ease-out;
  }
  .teams-dropdown:hover,
  .teams-dropdown.activeMenuItem {
    .label {
      @include mixins.font-medium;
      color: var(--theme-colour-logo);
    }

    &:after {
      width: 100%;
    }
  }

  .dropdown-toggle {
    width: var(--nav-item-width);
    border: none;
    background-color: transparent;
    height: var(--nav-height);
    cursor: pointer;
  }

  svg.arrow {
    width: 0.625rem;
    vertical-align: middle;
    @include mixins.fml-1;
    fill: var(--theme-colour-text-primary);
    transition: transform 0.15s ease-in;

    &.up {
      transform: rotate(180deg);
    }
  }
</style>
